import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  OnInit,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import {
  RdsAccordionDirective,
  RdsButtonComponent,
  RdsExpansionPanelComponent,
  RdsExpansionPanelHeaderComponent,
  RdsExpansionPanelTitleDirective,
  RdsFormFieldComponent,
  RdsLabelDirective,
  RdsMultiSelectComponent,
  RdsMultiSelectOptionComponent,
  RdsSelectComponent,
  RdsSingleSelectOptionComponent,
} from '@rds/angular-components';

import { ViewportService } from '@bookly/shared';

import { RoomsService } from '../../../room-management/services/rooms.service';
import { EventCalendarFiltersEnum } from '../../model/rooms-calendar.model';
import {
  CalendarFilters,
  RoomsCalendarService,
} from '../../services/rooms-calendar.service';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    FormsModule,
    RdsFormFieldComponent,
    RdsButtonComponent,
    RdsLabelDirective,
    RdsSelectComponent,
    RdsSingleSelectOptionComponent,
    RdsMultiSelectComponent,
    RdsMultiSelectOptionComponent,
    RdsAccordionDirective,
    RdsExpansionPanelComponent,
    RdsExpansionPanelHeaderComponent,
    RdsExpansionPanelTitleDirective,
  ],
  selector: 'bad-calendar-filters',
  styleUrl: './calendar-filters.component.scss',
  templateUrl: './calendar-filters.component.html',
})
export class CalendarFiltersComponent implements OnInit {
  readonly #roomsService = inject(RoomsService);
  readonly #roomsCalendarService = inject(RoomsCalendarService);
  readonly #destroyRef = inject(DestroyRef);
  readonly #fb = inject(FormBuilder);

  /**
   * Collapsed by default where the filters would otherwise push the content off
   * screen. Seeded once, so resizing does not fight the user's own toggling.
   */
  protected readonly filtersExpanded = signal(
    !inject(ViewportService).isCompact()
  );

  /** Initial filter values (e.g. from query params) */
  readonly initialFilters = input<CalendarFilters>();

  /** Emitted whenever filter values change */
  readonly filtersChange = output<CalendarFilters>();

  /** Emitted when clear button is clicked */
  readonly filtersCleared = output<void>();

  protected readonly EventCalendarFiltersEnum = EventCalendarFiltersEnum;
  protected services = this.#roomsService.roomServices;
  protected readonly siteNames = this.#roomsCalendarService.siteNames;
  protected readonly buildingNames = this.#roomsCalendarService.buildingNames;
  protected readonly floorNames = this.#roomsCalendarService.floorNames;

  protected filterForm: FormGroup = this.#fb.group({
    service: [[]],
    status: [EventCalendarFiltersEnum.ALL],
    siteName: [[]],
    buildingName: [[]],
    floorName: [[]],
    sort: ['asc'],
  });

  ngOnInit() {
    const initial = this.initialFilters();
    if (initial) {
      this.filterForm.patchValue(initial, { emitEvent: false });
    }
    this.#roomsService.getRoomServices().subscribe();
    this.#subscribeToFilterChanges();
  }

  protected clearFilters() {
    this.filterForm.reset({
      siteName: [],
      buildingName: [],
      floorName: [],
      service: [],
      status: EventCalendarFiltersEnum.ALL,
      sort: 'asc',
    });
    this.filtersCleared.emit();
  }

  #subscribeToFilterChanges() {
    this.filterForm
      .get('siteName')!
      .valueChanges.pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe(() => {
        const currentBuildings = this.buildingNames();
        const selectedBuildings: string[] =
          this.filterForm.get('buildingName')!.value || [];
        const validBuildings = selectedBuildings.filter(b =>
          currentBuildings.includes(b)
        );
        if (validBuildings.length !== selectedBuildings.length) {
          this.filterForm
            .get('buildingName')!
            .setValue(validBuildings, { emitEvent: true });
        }
      });

    this.filterForm
      .get('buildingName')!
      .valueChanges.pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe(() => {
        const currentFloors = this.floorNames();
        const selectedFloors: string[] =
          this.filterForm.get('floorName')!.value || [];
        const validFloors = selectedFloors.filter(f =>
          currentFloors.includes(f)
        );
        if (validFloors.length !== selectedFloors.length) {
          this.filterForm
            .get('floorName')!
            .setValue(validFloors, { emitEvent: true });
        }
      });

    this.filterForm.valueChanges
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe(values => {
        this.filtersChange.emit(values as CalendarFilters);
      });
  }
}
